/**
 * CS Audits Settings Modal
 *
 * Provides filter, sort, and display options for the CS Audits list view.
 * Used on the CyberSweepClientDetail page.
 */

import React, { useState } from 'react';
import { X, Settings, SlidersHorizontal, ArrowUpDown, Eye } from 'lucide-react';
import { StandardButton } from '../standard';

export interface CSAuditsSettings {
  filterByStatus: string;
  filterByTemplate: string;
  dateRangeStart: string;
  dateRangeEnd: string;
  sortBy: 'date' | 'status' | 'template' | 'name';
  sortOrder: 'asc' | 'desc';
  showCompleted: boolean;
  compactView: boolean;
}

export const defaultCSAuditsSettings: CSAuditsSettings = {
  filterByStatus: '',
  filterByTemplate: '',
  dateRangeStart: '',
  dateRangeEnd: '',
  sortBy: 'date',
  sortOrder: 'desc',
  showCompleted: true,
  compactView: false,
};

interface CSAuditsSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentSettings: CSAuditsSettings;
  onApply: (settings: CSAuditsSettings) => void;
}

// Status options for filtering
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'Phase 1 - Not Started', label: 'Phase 1 - Not Started' },
  { value: 'Phase 1 - In Progress', label: 'Phase 1 - In Progress' },
  { value: 'Phase 1 - Complete', label: 'Phase 1 - Complete' },
  { value: 'Phase 2 - Not Started', label: 'Phase 2 - Not Started' },
  { value: 'Phase 2 - In Progress', label: 'Phase 2 - In Progress' },
  { value: 'Phase 2 - Complete', label: 'Phase 2 - Complete' },
  { value: 'Audit Complete', label: 'Audit Complete' },
];

// Template options for filtering
const TEMPLATE_OPTIONS = [
  { value: '', label: 'All Templates' },
  { value: 'Initial Deletion', label: 'Initial Deletion' },
  { value: 'Initial Audit', label: 'Initial Audit' },
  { value: 'Final Audit', label: 'Final Audit' },
  { value: 'Quarter #1 Audit', label: 'Quarter #1 Audit' },
  { value: 'Quarter #2 Audit', label: 'Quarter #2 Audit' },
  { value: 'Quarter #3 Audit', label: 'Quarter #3 Audit' },
  { value: 'Quarter #4 Audit', label: 'Quarter #4 Audit' },
];

// Sort options
const SORT_OPTIONS = [
  { value: 'date', label: 'Due Date' },
  { value: 'status', label: 'Phase Status' },
  { value: 'template', label: 'Audit Template' },
  { value: 'name', label: 'Audit Name' },
];

const CSAuditsSettingsModal: React.FC<CSAuditsSettingsModalProps> = ({
  isOpen,
  onClose,
  currentSettings,
  onApply,
}) => {
  const [settings, setSettings] = useState<CSAuditsSettings>(currentSettings);

  if (!isOpen) return null;

  const handleFieldChange = (field: keyof CSAuditsSettings, value: string | boolean) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleApply = () => {
    onApply(settings);
    onClose();
  };

  const handleReset = () => {
    setSettings(defaultCSAuditsSettings);
  };

  // Section header component
  const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
      <span className="text-gray-500 dark:text-gray-400">{icon}</span>
      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <Settings className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  CS Audits Settings
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Filter, sort, and display options
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-6">
            {/* Filter Options */}
            <div>
              <SectionHeader icon={<SlidersHorizontal className="h-4 w-4" />} title="Filter Options" />

              <div className="space-y-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Filter by Status
                  </label>
                  <select
                    value={settings.filterByStatus}
                    onChange={(e) => handleFieldChange('filterByStatus', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Template Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Filter by Template
                  </label>
                  <select
                    value={settings.filterByTemplate}
                    onChange={(e) => handleFieldChange('filterByTemplate', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {TEMPLATE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Due Date From
                    </label>
                    <input
                      type="date"
                      value={settings.dateRangeStart}
                      onChange={(e) => handleFieldChange('dateRangeStart', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Due Date To
                    </label>
                    <input
                      type="date"
                      value={settings.dateRangeEnd}
                      onChange={(e) => handleFieldChange('dateRangeEnd', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Sort Options */}
            <div>
              <SectionHeader icon={<ArrowUpDown className="h-4 w-4" />} title="Sort Options" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Sort By
                  </label>
                  <select
                    value={settings.sortBy}
                    onChange={(e) => handleFieldChange('sortBy', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Sort Order
                  </label>
                  <select
                    value={settings.sortOrder}
                    onChange={(e) => handleFieldChange('sortOrder', e.target.value as 'asc' | 'desc')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Display Options */}
            <div>
              <SectionHeader icon={<Eye className="h-4 w-4" />} title="Display Options" />

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.showCompleted}
                    onChange={(e) => handleFieldChange('showCompleted', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Show completed audits
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.compactView}
                    onChange={(e) => handleFieldChange('compactView', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Compact view
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-lg">
            <StandardButton variant="ghost" onClick={handleReset}>
              Reset to Defaults
            </StandardButton>
            <div className="flex items-center gap-3">
              <StandardButton variant="secondary" onClick={onClose}>
                Cancel
              </StandardButton>
              <StandardButton variant="primary" onClick={handleApply}>
                Apply Settings
              </StandardButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSAuditsSettingsModal;
